<template>
  <section class="mobile-notifications-page">
    <header class="mb-5">
      <div class="overline primary--text font-weight-bold">INBOX</div>
      <h1 class="text-h4 font-weight-bold mb-1">Notifications</h1>
      <p class="admin-muted mb-0">Store alerts saved on this phone.</p>
    </header>

    <v-alert v-if="status !== 'enabled'" type="info" outlined class="rounded-xl mb-5">
      <div class="font-weight-medium">{{ status === 'unavailable' ? 'Alerts are not available in this test build' : 'Stay up to date' }}</div>
      <div class="text-body-2">{{ status === 'unavailable' ? 'You can still browse your saved alerts.' : 'Enable alerts to receive important store activity on this phone.' }}</div>
      <v-btn v-if="status !== 'unavailable'" color="primary" class="mt-3" :loading="enabling" @click="enable">Enable notifications</v-btn>
    </v-alert>

    <v-text-field v-model.trim="query" outlined dense rounded clearable hide-details prepend-inner-icon="mdi-magnify" label="Search notifications" class="mb-5" />

    <v-card v-if="!filtered.length" outlined class="rounded-xl pa-7 text-center elevation-0">
      <v-icon size="52" color="primary" class="mb-3">mdi-bell-outline</v-icon>
      <h2 class="text-h6 font-weight-bold mb-2">{{ history.length ? "No matching notifications" : "No notifications yet" }}</h2>
      <p class="admin-muted mb-0">{{ history.length ? "Try a different search." : "Alerts you receive will appear here so you can find them later." }}</p>
    </v-card>

    <div v-for="group in grouped" :key="group.label" class="mb-5">
      <h2 class="text-subtitle-2 font-weight-bold admin-muted mb-2">{{ group.label }}</h2>
      <v-card outlined class="rounded-xl elevation-0 overflow-hidden">
        <v-list two-line class="py-0">
          <template v-for="(item, index) in group.items">
            <v-divider v-if="index" :key="`${item.id}-divider`" inset />
            <v-list-item :key="item.id" class="notification-item--linked" @click="showDetails(item)">
              <v-list-item-avatar color="primary"><v-icon dark>mdi-bell-outline</v-icon></v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title class="font-weight-medium">{{ item.title }}</v-list-item-title>
                <v-list-item-subtitle v-if="item.body">{{ item.body }}</v-list-item-subtitle>
                <div v-if="context(item)" class="text-caption font-weight-medium primary--text mt-1">{{ context(item) }}</div>
                <div class="text-caption admin-muted mt-1">{{ time(item.receivedAt) }}</div>
              </v-list-item-content>
              <v-list-item-icon v-if="item.route"><v-icon>mdi-chevron-right</v-icon></v-list-item-icon>
            </v-list-item>
          </template>
        </v-list>
      </v-card>
    </div>

    <v-bottom-sheet v-model="detailOpen" inset content-class="notification-detail-sheet">
      <v-card v-if="selectedNotification" class="notification-detail-sheet__card rounded-t-xl">
        <v-card-title class="notification-detail-sheet__header">
          <span>Notification details</span>
          <v-btn icon aria-label="Close notification details" @click="detailOpen = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="notification-detail-sheet__body">
          <h2 class="text-h6 font-weight-bold mb-2">{{ selectedNotification.title }}</h2>
          <div v-if="context(selectedNotification)" class="text-subtitle-2 primary--text font-weight-medium mb-2">{{ context(selectedNotification) }}</div>
          <div class="text-caption admin-muted mb-4">{{ fullDate(selectedNotification.receivedAt) }}</div>
          <p v-if="selectedNotification.body" class="notification-detail-sheet__message mb-5">{{ selectedNotification.body }}</p>
        </v-card-text>
        <v-card-actions v-if="selectedNotification.route" class="notification-detail-sheet__actions">
          <v-btn block rounded color="primary" @click="open(selectedNotification)">
            Open details
            <v-icon right>mdi-arrow-right</v-icon>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-bottom-sheet>
  </section>
</template>

<script>
import { enableMobileNotifications, getMobileNotificationHistory, getMobileNotificationStatus } from "./service";

export default {
  name: "MobileNotifications",
  data: () => ({ query: "", history: [], detailOpen: false, selectedNotification: null, status: "prompt", enabling: false }),
  computed: {
    filtered() {
      const query = (this.query || "").toLowerCase();
      return this.history.filter((item) => !query || `${item.title} ${item.body} ${item.organization || ""} ${item.location || ""}`.toLowerCase().includes(query));
    },
    grouped() {
      const groups = [];
      this.filtered.forEach((item) => {
        const date = new Date(item.receivedAt);
        const today = new Date();
        const yesterday = new Date();
        yesterday.setDate(today.getDate() - 1);
        let label = date.toDateString() === today.toDateString() ? "Today" : date.toDateString() === yesterday.toDateString() ? "Yesterday" : date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
        let group = groups.find((entry) => entry.label === label);
        if (!group) { group = { label, items: [] }; groups.push(group); }
        group.items.push(item);
      });
      return groups;
    },
  },
  mounted() {
    this.load();
    this.refreshStatus();
    window.addEventListener("repairnest-notification-history-changed", this.load);
  },
  beforeDestroy() { window.removeEventListener("repairnest-notification-history-changed", this.load); },
  methods: {
    async refreshStatus() {
      try { this.status = await getMobileNotificationStatus(); } catch (error) { this.status = "error"; }
    },
    async enable() {
      this.enabling = true;
      try { this.status = await enableMobileNotifications(); } catch (error) { this.status = "error"; }
      finally { this.enabling = false; }
    },
    load() { this.history = getMobileNotificationHistory(); },
    time(value) { return new Date(value).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }); },
    fullDate(value) { return new Date(value).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); },
    context(item) { return [item.organization, item.location].filter(Boolean).join(" · "); },
    showDetails(item) { this.selectedNotification = item; this.detailOpen = true; },
    open(item) {
      if (!item.route) return;
      this.detailOpen = false;
      let path = item.route;
      try { if (/^https?:\/\//i.test(path)) path = new URL(path).pathname; } catch (error) { return; }
      if (path.startsWith("/") && path !== this.$route.fullPath) this.$router.push(path).catch(() => {});
    },
  },
};
</script>

<style scoped>
.mobile-notifications-page { margin: 0 auto; max-width: 760px; }
.notification-item--linked { cursor: pointer; }
</style>

<style>
.notification-detail-sheet { margin: 0 !important; max-width: 640px; }
.notification-detail-sheet__card { display: flex; flex-direction: column; max-height: min(80vh, 720px); overflow: hidden; }
.notification-detail-sheet__header { align-items: center; border-bottom: 1px solid rgba(0, 0, 0, 0.12); justify-content: space-between; }
.theme--dark .notification-detail-sheet__header { border-bottom-color: rgba(255, 255, 255, 0.16); }
.notification-detail-sheet__body { min-height: 0; overflow-y: auto; padding: 20px; }
.notification-detail-sheet__actions { border-top: 1px solid rgba(0, 0, 0, 0.12); flex: 0 0 auto; padding: 12px 20px calc(12px + env(safe-area-inset-bottom)); }
.theme--dark .notification-detail-sheet__actions { border-top-color: rgba(255, 255, 255, 0.16); }
.notification-detail-sheet__message { font-size: 1rem; line-height: 1.55; white-space: pre-wrap; overflow-wrap: anywhere; }
</style>
